import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';

const LUMINA_LOGO = "https://customer-assets.emergentagent.com/job_revenue-cycle-1/artifacts/wnwhtsce_lumina_logo_white.png";
const REVQ_LOGO = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/700vz85m_revq_logo_white.png";
const ECHO_LOGO = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/ddnrysy5_echo_logo_white.svg";

const APPS = [
  { id: 'lumina', name: 'Lumina', subtitle: 'Clinical Intelligence', logo: LUMINA_LOGO, href: '/lumina', accent: '#6b5ce7' },
  { id: 'revq', name: 'RevQ', subtitle: 'Revenue Cycle', logo: REVQ_LOGO, href: '/revq', accent: '#10b981' },
  { id: 'echo', name: 'Echo', subtitle: 'Front Office', logo: ECHO_LOGO, href: '/echo', accent: '#3b82f6' },
];

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine current app
  const currentApp = location.pathname.startsWith('/lumina') ? 'lumina' :
                     location.pathname.startsWith('/revq') ? 'revq' :
                     location.pathname.startsWith('/echo') ? 'echo' : null;

  // Set initial selection to next app (like Cmd+Tab in macOS)
  useEffect(() => {
    if (open) {
      const currentIdx = APPS.findIndex(a => a.id === currentApp);
      setSelectedIndex(currentIdx >= 0 ? (currentIdx + 1) % APPS.length : 0);
    }
  }, [open, currentApp]);

  const handleKeyDown = useCallback((e) => {
    // Cmd+K to toggle
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setOpen(prev => !prev);
      return;
    }
    if (!open) return;

    if (e.key === 'ArrowRight' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      setSelectedIndex(i => (i + 1) % APPS.length);
    } else if (e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setSelectedIndex(i => (i - 1 + APPS.length) % APPS.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      navigate(APPS[selectedIndex].href);
      setOpen(false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }, [open, selectedIndex, navigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop — full screen blur + dim */}
      <div
        className="fixed inset-0 z-[9998]"
        style={{ background: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
        onClick={() => setOpen(false)}
        data-testid="app-switcher-backdrop"
      />

      {/* Switcher container */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
        data-testid="app-switcher"
      >
        <div
          className="pointer-events-auto"
          style={{
            background: 'rgba(30, 30, 30, 0.82)',
            borderRadius: 22,
            padding: '28px 32px 22px',
            border: '0.5px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.5), 0 0 0 0.5px rgba(255,255,255,0.06) inset',
          }}
          data-testid="app-switcher-container"
        >
          {/* App icons row */}
          <div className="flex items-center" style={{ gap: 20 }}>
            {APPS.map((app, i) => {
              const isSelected = i === selectedIndex;
              const isCurrent = app.id === currentApp;
              return (
                <button
                  key={app.id}
                  onClick={() => { navigate(app.href); setOpen(false); }}
                  onMouseEnter={() => setSelectedIndex(i)}
                  className="flex flex-col items-center outline-none"
                  style={{ width: 120, padding: '12px 8px', borderRadius: 16, background: isSelected ? 'rgba(255, 255, 255, 0.1)' : 'transparent', transition: 'background 120ms ease' }}
                  data-testid={`switcher-${app.id}`}
                >
                  {/* Icon container */}
                  <div
                    style={{
                      width: 64, height: 64, borderRadius: 16,
                      background: isSelected ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.06)',
                      border: isSelected ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 120ms ease',
                      boxShadow: isSelected ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
                    }}
                  >
                    <img src={app.logo} alt={app.name} style={{ height: 22, width: 'auto', objectFit: 'contain', opacity: isSelected ? 1 : 0.6, transition: 'opacity 120ms' }} />
                  </div>

                  {/* App name */}
                  <p style={{
                    fontSize: 12, fontWeight: 500, marginTop: 8, textAlign: 'center',
                    color: isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
                    transition: 'color 120ms',
                  }}>
                    {app.name}
                  </p>

                  {/* Current app indicator */}
                  {isCurrent && (
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#ffffff', marginTop: 4, opacity: 0.5 }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Subtitle of selected app */}
          <p style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255, 255, 255, 0.35)', marginTop: 8, fontWeight: 400, transition: 'color 120ms' }}>
            {APPS[selectedIndex]?.subtitle}
          </p>

          {/* Keyboard hints */}
          <div className="flex items-center justify-center" style={{ gap: 12, marginTop: 14 }}>
            <div className="flex items-center" style={{ gap: 4 }}>
              <kbd style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 18, borderRadius: 4, background: 'rgba(255,255,255,0.08)', border: '0.5px solid rgba(255,255,255,0.12)', fontSize: 9, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>
                ←
              </kbd>
              <kbd style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 18, borderRadius: 4, background: 'rgba(255,255,255,0.08)', border: '0.5px solid rgba(255,255,255,0.12)', fontSize: 9, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>
                →
              </kbd>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginLeft: 2 }}>navigate</span>
            </div>
            <div style={{ width: 0.5, height: 12, background: 'rgba(255,255,255,0.1)' }} />
            <div className="flex items-center" style={{ gap: 4 }}>
              <kbd style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 18, paddingInline: 6, borderRadius: 4, background: 'rgba(255,255,255,0.08)', border: '0.5px solid rgba(255,255,255,0.12)', fontSize: 9, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>
                enter
              </kbd>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginLeft: 2 }}>open</span>
            </div>
            <div style={{ width: 0.5, height: 12, background: 'rgba(255,255,255,0.1)' }} />
            <div className="flex items-center" style={{ gap: 4 }}>
              <kbd style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 18, paddingInline: 6, borderRadius: 4, background: 'rgba(255,255,255,0.08)', border: '0.5px solid rgba(255,255,255,0.12)', fontSize: 9, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>
                esc
              </kbd>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginLeft: 2 }}>close</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
